/**
 * Scan the company's `Inkorg/` in the remote store for files this app created
 * on any device (spec section 8). What is visible there is the backend's call:
 * under Drive's drive.file scope only app-created (or picked) files show up,
 * while a backend with plain folder access would also see what other apps drop.
 *
 * Diff is by remote id and content hash against `inboxItems.driveFileId` /
 * `driveMd5` (the columns keep their names), never by modified time. New files
 * are downloaded and handed to the injected `onNewFile` callback, which creates
 * attachment + inbox item (workstream F). This module never imports the inbox
 * pipeline.
 */
import { getRemoteStore, type RemoteEntry, type RemoteId } from '@/cloud/store';
import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import type { Uuid } from '@/domain/types';
import { ensureCompanyTree, updateDriveState } from './tree';

export const INBOX_SCAN_INTERVAL_MS = 5 * 60_000;

export type NewInboxFile = RemoteEntry & { blob: Blob; changed: boolean };
export type OnNewFile = (file: NewInboxFile) => Promise<void>;

export interface ScanResult {
  /** Files handed to `onNewFile` (new ids plus changed content hashes). */
  found: number;
  /** Files already known locally (skipped). */
  known: number;
  /** Per-file failures; the scan continues past them. */
  errors: { fileId: string; name: string; error: string }[];
}

/**
 * Decide which listed files are new for this company. Pure so it is testable:
 * `knownById` maps driveFileId → stored hash (undefined when never recorded),
 * `localIds` are inbox item / attachment ids that in-flight uploads stamp on
 * their files (metadata.inboxItemId / attachmentId) so a file uploaded from
 * this device is never imported twice.
 */
export function diffInboxFiles(
  files: RemoteEntry[],
  knownById: ReadonlyMap<string, string | undefined>,
  localIds: ReadonlySet<string> = new Set(),
): { fresh: { file: RemoteEntry; changed: boolean }[]; known: number } {
  const fresh: { file: RemoteEntry; changed: boolean }[] = [];
  let known = 0;
  for (const file of files) {
    if (file.isFolder) continue;
    const own = file.metadata?.inboxItemId ?? file.metadata?.attachmentId;
    if (own && localIds.has(own)) {
      known++;
      continue;
    }
    if (knownById.has(file.id)) {
      const storedHash = knownById.get(file.id);
      if (storedHash && file.contentHash && storedHash !== file.contentHash) fresh.push({ file, changed: true });
      else known++;
      continue;
    }
    fresh.push({ file, changed: false });
  }
  return { fresh, known };
}

export interface ScanDeps {
  list: (parentId: RemoteId) => Promise<RemoteEntry[]>;
  download: (fileId: RemoteId) => Promise<Blob>;
}

const defaultScanDeps: ScanDeps = {
  list: (parentId) => getRemoteStore().list(parentId),
  download: (fileId) => getRemoteStore().get(fileId),
};

const scanInflight = new Map<Uuid, Promise<ScanResult>>();

export async function scanInbox(
  companyId: Uuid,
  onNewFile: OnNewFile,
  db: BookkeepingDB = defaultDb,
  deps: ScanDeps = defaultScanDeps,
): Promise<ScanResult> {
  const running = scanInflight.get(companyId);
  if (running) return running;
  const promise = runScan(companyId, onNewFile, db, deps).finally(() => scanInflight.delete(companyId));
  scanInflight.set(companyId, promise);
  return promise;
}

async function runScan(companyId: Uuid, onNewFile: OnNewFile, db: BookkeepingDB, deps: ScanDeps): Promise<ScanResult> {
  const company = await db.companies.get(companyId);
  if (!company) throw new Error(`Company ${companyId} not found`);
  const folders = await ensureCompanyTree(company, db);
  const files = await deps.list(folders.inbox);

  const ids = files.map((f) => f.id);
  const [items, attachments] = await Promise.all([
    ids.length ? db.inboxItems.where('driveFileId').anyOf(ids).toArray() : Promise.resolve([]),
    ids.length ? db.attachments.where('driveFileId').anyOf(ids).toArray() : Promise.resolve([]),
  ]);
  const knownById = new Map<string, string | undefined>();
  for (const a of attachments) if (a.driveFileId) knownById.set(a.driveFileId, undefined);
  for (const it of items) if (it.driveFileId) knownById.set(it.driveFileId, it.driveMd5);

  const ownIds = files
    .map((f) => f.metadata?.inboxItemId ?? f.metadata?.attachmentId)
    .filter((id): id is string => !!id);
  const localIds = new Set<string>();
  if (ownIds.length) {
    const [ownItems, ownAttachments] = await Promise.all([
      db.inboxItems.where('id').anyOf(ownIds).primaryKeys(),
      db.attachments.where('id').anyOf(ownIds).primaryKeys(),
    ]);
    for (const id of [...ownItems, ...ownAttachments]) localIds.add(id);
  }

  const { fresh, known } = diffInboxFiles(files, knownById, localIds);
  const result: ScanResult = { found: 0, known, errors: [] };
  for (const { file, changed } of fresh) {
    try {
      const blob = await deps.download(file.id);
      await onNewFile({ ...file, blob, changed });
      result.found++;
    } catch (e) {
      result.errors.push({ fileId: file.id, name: file.name, error: e instanceof Error ? e.message : String(e) });
    }
  }
  await updateDriveState(companyId, (s) => void (s.lastInboxScanAt = new Date().toISOString()), db);
  return result;
}

export interface InboxScanLoopOptions {
  /** The company to scan right now; null pauses the loop (no company / no backend connected). */
  companyId: () => Uuid | null | Promise<Uuid | null>;
  onNewFile: OnNewFile;
  onResult?: (result: ScanResult) => void;
  onError?: (error: unknown) => void;
  intervalMs?: number;
  db?: BookkeepingDB;
  deps?: ScanDeps;
}

/**
 * Run the scan on start, when the tab becomes visible and every 5 minutes.
 * Returns a handle with `stop()` and `scanNow()` (the manual refresh icon).
 */
export function startInboxScanLoop(opts: InboxScanLoopOptions): { stop(): void; scanNow(): Promise<ScanResult | null> } {
  const interval = opts.intervalMs ?? INBOX_SCAN_INTERVAL_MS;
  let stopped = false;

  const scanNow = async (): Promise<ScanResult | null> => {
    if (stopped) return null;
    try {
      const companyId = await opts.companyId();
      if (!companyId) return null;
      const result = await scanInbox(companyId, opts.onNewFile, opts.db, opts.deps);
      opts.onResult?.(result);
      return result;
    } catch (e) {
      opts.onError?.(e);
      return null;
    }
  };

  const onVisible = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') void scanNow();
  };
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
  const timer = setInterval(() => void scanNow(), interval);
  void scanNow();

  return {
    stop() {
      stopped = true;
      clearInterval(timer);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
    },
    scanNow,
  };
}

// ---- picked files ------------------------------------------------------------

/** What a file chooser hands back: enough to fetch the file and name the copy. */
export interface PickedFile {
  id: RemoteId;
  name: string;
  mimeType: string;
}

export interface ImportPickedDeps {
  download: (fileId: RemoteId) => Promise<Blob>;
  upload: (input: { name: string; parentId: RemoteId; blob: Blob; mediaType: string; metadata: Record<string, string> }) => Promise<RemoteEntry>;
}

const defaultImportDeps: ImportPickedDeps = {
  download: (fileId) => getRemoteStore().get(fileId),
  upload: (input) => getRemoteStore().put(input),
};

export interface ImportPickedResult {
  imported: number;
  errors: { id: string; name: string; error: string }[];
}

/**
 * Download each picked file and re-upload the app's own copy to `Inkorg/` so it
 * is filed like any other receipt. `onNewFile` gets the new copy plus its bytes.
 *
 * The copy matters on Drive, where drive.file grants access to a picked file
 * but not to anything else in the folder it came from; owning the copy is what
 * lets the app move it to `Kvitton/` once it is booked.
 */
export async function importPickedFiles(
  docs: readonly PickedFile[],
  target: { inboxFolderId: RemoteId; companyId: string },
  onNewFile: OnNewFile,
  deps: ImportPickedDeps = defaultImportDeps,
): Promise<ImportPickedResult> {
  const result: ImportPickedResult = { imported: 0, errors: [] };
  for (const doc of docs) {
    try {
      const blob = await deps.download(doc.id);
      const copy = await deps.upload({
        name: doc.name,
        parentId: target.inboxFolderId,
        blob,
        mediaType: doc.mimeType || blob.type || 'application/octet-stream',
        metadata: { source: 'picker', pickedFrom: doc.id, companyId: target.companyId },
      });
      await onNewFile({ ...copy, blob, changed: false });
      result.imported++;
    } catch (e) {
      result.errors.push({ id: doc.id, name: doc.name, error: e instanceof Error ? e.message : String(e) });
    }
  }
  return result;
}
